import { useState } from "react";
import styled from "styled-components";
import { fetchWithAuth, setSigningOut } from "../utils/authFetch";

const Text = styled.p`
  margin: 0 0 12px;
  font-size: 13.5px;
  line-height: 1.55;
  color: ${({ theme }) => theme.inkSoft};
  max-width: 52ch;
`;

const Input = styled.input`
  width: 100%;
  max-width: 400px;
  box-sizing: border-box;
  height: 36px;
  padding: 0 12px;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.line};
  background: ${({ theme }) => theme.bg};
  color: ${({ theme }) => theme.ink};
  font: inherit;
  font-size: 13.5px;
  outline: none;
  transition: border-color 0.12s, box-shadow 0.12s;
  &::placeholder {
    color: ${({ theme }) => theme.inkMuted};
    opacity: 0.7;
  }
  &:focus {
    border-color: ${({ theme }) => theme.danger};
    box-shadow: 0 0 0 3px ${({ theme }) => theme.dangerSoft};
  }
`;

const Actions = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: 14px;
`;

const Button = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 34px;
  padding: 0 14px;
  border-radius: 8px;
  font-family: "Geist", ui-sans-serif, system-ui, sans-serif;
  font-size: 13px;
  font-weight: 500;
  line-height: 1;
  white-space: nowrap;
  cursor: pointer;
  transition: background 0.12s, border-color 0.12s, color 0.12s;
  border: 1px solid ${({ theme, $solid }) => ($solid ? theme.danger : theme.line)};
  background: ${({ theme, $solid }) => ($solid ? theme.danger : theme.surface)};
  color: ${({ theme, $solid, $danger }) => ($solid ? "#fff" : $danger ? theme.danger : theme.ink)};
  &:hover {
    background: ${({ theme, $solid }) => ($solid ? theme.dangerHover : theme.bg)};
    border-color: ${({ theme, $solid, $danger }) => ($solid ? theme.dangerHover : $danger ? theme.danger : theme.line)};
  }
  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const ErrorText = styled.p`
  margin: 10px 0 0;
  font-size: 13px;
  color: ${({ theme }) => theme.danger};
`;

// The control for the Settings "Close your account" row: Settings supplies the row, label and hint.
export default function DeleteAccount() {
  const [open, setOpen] = useState(false);
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const confirmed = confirm.trim().toLowerCase() === "delete account";

  const cancel = () => {
    setOpen(false);
    setConfirm("");
    setError("");
  };

  const remove = async () => {
    setBusy(true);
    setError("");
    // The login ends the moment the account is deleted; stop background requests bouncing to the login screen.
    setSigningOut(true);
    try {
      const res = await fetchWithAuth("/api/account", {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ confirm }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || "We couldn't delete your account. Please try again.");
      }
      // Signs out of the login provider too, then returns to the home page.
      window.location.href = `${process.env.REACT_APP_API_URL || ""}/api/auth/logout`;
    } catch (err) {
      setSigningOut(false);
      setError(err.message);
      setBusy(false);
    }
  };

  return (
    <>
      <Text>
        Removes your saved candidates, searches, outreach history, settings and remaining credits. If you are the only
        member of your workspace, the workspace and its imported jobs are removed as well.
      </Text>
      {!open ? (
        <Button type="button" $danger onClick={() => setOpen(true)}>
          Delete account
        </Button>
      ) : (
        <>
          <Text>
            To continue, type <strong>"Delete Account"</strong> in the field below.
          </Text>
          <Input
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            placeholder="Delete Account"
            aria-label="Type Delete Account to confirm"
            autoComplete="off"
            autoFocus
            disabled={busy}
          />
          {error && <ErrorText role="alert">{error}</ErrorText>}
          <Actions>
            <Button type="button" onClick={cancel} disabled={busy}>
              Cancel
            </Button>
            <Button type="button" $solid onClick={remove} disabled={busy || !confirmed}>
              {busy ? "Deleting…" : "Delete account"}
            </Button>
          </Actions>
        </>
      )}
    </>
  );
}
